"use strict";
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const common_utils_1 = require("@zapier/common-utils");
const react_1 = require("react");
const lodash_1 = require("lodash");
const theme_1 = require("@zapier/theme");
const design_system_1 = require("@zapier/design-system");
const common_components_1 = require("@zapier/common-components");
const hooks_1 = require("../../hooks");
const react_beautiful_dnd_1 = require("react-beautiful-dnd");
const moveItem_1 = __importDefault(require("./moveItem"));
const updateValue_1 = __importDefault(require("./updateValue"));
const isItemDraggable_1 = __importDefault(require("./isItemDraggable"));
const removeValue_1 = __importDefault(require("./removeValue"));
const getValuesToRender_1 = __importDefault(require("./getValuesToRender"));
const updateValueInPlace_1 = __importDefault(require("./updateValueInPlace"));
const sortingHandleSize = 30;
const sortingHandleClassName = 'sorting-handle';
const sortingHandleFocusSelector = `&:focus .${sortingHandleClassName}`;
const sortingHandleHoverSelector = `&:hover .${sortingHandleClassName}`;
const Styles = common_utils_1.labelStyles(__filename, {
    container: {
        [sortingHandleHoverSelector]: {
            opacity: 1,
        },
    },
    perFieldContainer: {
        position: 'relative',
    },
    droppable({ isDraggingOver }) {
        const edge = isDraggingOver ? -5 : 0;
        const dragInProgressOutlineStyle = {
            content: '""',
            position: 'absolute',
            pointerEvents: 'none',
            top: edge,
            right: edge,
            bottom: edge,
            left: edge,
            borderWidth: 2,
            borderStyle: 'solid',
            borderColor: isDraggingOver ? design_system_1.Colors.blue500 : design_system_1.Colors.transparent,
            borderRadius: 12,
            transitionProperty: 'all',
            transitionTimingFunction: design_system_1.Animation.transitionTimingFunction,
            transitionDuration: design_system_1.Animation.transitionDuration,
        };
        return {
            borderRadius: theme_1.Border.radius.large,
            position: 'relative',
            '&::before': dragInProgressOutlineStyle,
        };
    },
    draggable({ isDragging }) {
        return {
            cursor: isDragging ? 'grabbing' : 'inherit',
            userSelect: 'none',
            marginBottom: theme_1.Spacing.small,
            [sortingHandleHoverSelector]: isDragging
                ? {
                    opacity: 1,
                }
                : {},
            [sortingHandleFocusSelector]: {
                color: design_system_1.Colors.blue500,
                opacity: 1,
            },
        };
    },
    sortingHandle(isDragging) {
        return {
            color: isDragging ? design_system_1.Colors.blue500 : design_system_1.Colors.inherit,
            cursor: 'grab',
            position: 'absolute',
            top: '50%',
            marginTop: -(sortingHandleSize / 2),
            marginLeft: -sortingHandleSize,
            transition: `opacity ${design_system_1.Animation.transitionDuration} ${design_system_1.Animation.transitionTimingFunction}`,
            [theme_1.Screen.supports.hover]: {
                opacity: 0,
            },
            '&:hover': {
                color: design_system_1.Colors.blue500,
            },
        };
    },
});
const SortingHandle = (_a) => {
    var { isDragging, isEnabled } = _a, props = __rest(_a, ["isDragging", "isEnabled"]);
    return isEnabled ? (common_utils_1.isolateComponent("div", Object.assign({ className: sortingHandleClassName, css: Styles.sortingHandle(isDragging) }, props),
        common_utils_1.isolateComponent(design_system_1.Icon, { name: "actionDrag", size: sortingHandleSize }))) : null;
};
const gridGap = theme_1.Spacing.extraSmall;
const buttonColor = 'greyAlt';
const gridColumns = (itemCount) => (itemCount > 1 ? '1fr 40px' : '1fr');
const PREVENT_REACT_BEAUTIFUL_DND_FROM_CAPTURING = { contentEditable: true };
const ReordableArrayItem = ({ children }) => (common_utils_1.isolateComponent("div", Object.assign({}, PREVENT_REACT_BEAUTIFUL_DND_FROM_CAPTURING), children));
function ReordableArray(props) {
    const { value: values = [], renderItem, onChange: performChange } = props;
    const [keySuffix, incrementKeySuffix] = hooks_1.useCounter(0);
    const onDragEnd = react_1.useCallback((result) => {
        if (result.destination) {
            const newValue = moveItem_1.default(values, result.source.index, result.destination.index);
            performChange(newValue, { commit: true });
            incrementKeySuffix();
        }
    }, [incrementKeySuffix, performChange, values]);
    const onChange = react_1.useCallback((selected, index, options = {}) => {
        const { updateInPlace } = options, performChangeOptions = __rest(options, ["updateInPlace"]);
        if (options.updateInPlace) {
            const newValue = updateValueInPlace_1.default({
                values,
                newValue: selected,
                index,
            });
            performChange(newValue, performChangeOptions);
        }
        else if (!lodash_1.isUndefined(selected) && !lodash_1.isNull(selected)) {
            const valuesToUpdate = lodash_1.isArray(values) ? values : [values];
            performChange(updateValue_1.default(valuesToUpdate, selected, index), performChangeOptions);
        }
        else {
            performChange(removeValue_1.default(values, index), performChangeOptions);
            incrementKeySuffix();
        }
    }, [incrementKeySuffix, performChange, values]);
    const onRemove = react_1.useCallback((index) => {
        performChange(removeValue_1.default(values, index), { commit: true });
        incrementKeySuffix();
    }, [incrementKeySuffix, performChange, values]);
    const getKey = (index) => `multi-field-${index}-key-${keySuffix}`;
    const valuesToRender = getValuesToRender_1.default(values);
    return (common_utils_1.isolateComponent("div", { css: Styles.container },
        common_utils_1.isolateComponent(react_beautiful_dnd_1.DragDropContext, { onDragEnd: onDragEnd },
            common_utils_1.isolateComponent(react_beautiful_dnd_1.Droppable, { droppableId: "droppable" }, (droppableProvided, droppableStateSnapshot) => (common_utils_1.isolateComponent("div", { css: Styles.droppable(droppableStateSnapshot), ref: droppableProvided.innerRef },
                valuesToRender.map((value, index) => (common_utils_1.isolateComponent(react_beautiful_dnd_1.Draggable, { draggableId: getKey(index), index: index, isDragDisabled: !isItemDraggable_1.default(values, index), key: index }, (draggableProvided, draggableStateSnapshot) => (common_utils_1.isolateComponent("div", Object.assign({ css: Styles.draggable(draggableStateSnapshot), ref: draggableProvided.innerRef }, draggableProvided.draggableProps, draggableProvided.dragHandleProps),
                    common_utils_1.isolateComponent("div", { css: Styles.perFieldContainer, key: getKey(index) },
                        common_utils_1.isolateComponent(SortingHandle, { isDragging: draggableStateSnapshot.isDragging, isEnabled: isItemDraggable_1.default(values, index) }),
                        common_utils_1.isolateComponent(common_components_1.GridLayout, { columns: gridColumns(valuesToRender.length), gap: gridGap },
                            common_utils_1.isolateComponent(ReordableArrayItem, null, renderItem({
                                index,
                                onChange,
                                value,
                            })),
                            index < valuesToRender.length - 1 && (common_utils_1.isolateComponent(design_system_1.IconButton, { color: buttonColor, icon: "formX", label: "Remove this item", onClick: () => onRemove(index), size: "small", tooltipPosition: "north" }))))))))),
                droppableProvided.placeholder))))));
}
exports.default = ReordableArray;
